import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import logo from "@/assets/thevibingco_logo.svg.asset.json";
import { MagneticButton } from "@/components/magnetic-button";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { RelatedGuides } from "@/components/related-guides";
import { SiteFooter } from "@/components/site-footer";
import { PricingPlans } from "@/components/pricing-plans";

const SERVICES = [
  {
    k: "01",
    t: "Idea to live product in 14 days",
    b: "A website or web app designed and built to a studio standard, live on day 14 for a fixed €35,000. Product strategy, UI/UX and the whole build from one team.",
    to: "/services/mvp-development",
    tag: "€35,000 fixed",
  },
  {
    k: "02",
    t: "Product design & development",
    b: "Full-stack designers and vetted Lovable experts who own your product end to end, in two-week sprints you can pause anytime.",
    to: "/services/lovable-development",
    tag: "Ongoing product team",
  },
  {
    k: "03",
    t: "Vibe coding rescue",
    b: "You vibe-coded into a corner. We diagnose, refactor and unstick stalled Lovable projects — usually inside a week.",
    to: "/services/vibe-coding-rescue",
    tag: "Fix a vibe-coded app",
  },
  {
    k: "04",
    t: "App development consulting",
    b: "Senior advisory for founders and product teams. Architecture reviews, build/buy calls, hiring help, second opinions.",
    to: "/services/app-development-consultant",
    tag: "Get an expert opinion",
  },
  {
    k: "05",
    t: "Lovable Cloud migration",
    b: "Move off Lovable Cloud onto your own Supabase. Row-level security, payments, edge functions, full ownership.",
    to: "/services/lovable-cloud-migration",
    tag: "Own your backend",
  },
];

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Services · Product Design and Development Studio | Vibing Co" },
      {
        name: "description",
        content:
          "An external product team of full-stack designers: idea to live product in 14 days for a fixed €35,000, ongoing product design and development, vibe coding rescue, consulting and Cloud migration. Trusted by 25+ teams.",
      },
      { property: "og:title", content: "Services · Your external product team | Vibing Company" },
      ...ogImageMeta("services", "Share card: product team services and engagements"),
      {
        property: "og:description",
        content:
          "Idea to live product in 14 days, ongoing product design and development, rescue, consulting and migration. By full-stack designers who ship in Lovable.",
      },
      { property: "og:url", content: "https://vibing.inc/services" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Vibe coding agency services",
          itemListElement: SERVICES.map((s, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `https://vibing.inc${s.to}`,
            name: s.t,
          })),
        }),
      },
    ],
  }),
  component: ServicesHub,
});

function ServicesHub() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
      <Hero />
      <Explainer />
      <Grid />
      <PricingPlans heading="Simple, flat pricing" />
      <RelatedGuides current="" />
      <CTA />

      </main>
      <SiteFooter />
    </div>
  );
}


function Hero() {
  return (
    <section aria-labelledby="svc-h" className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-16 md:pb-24">
      <h1
        id="svc-h"
        className="font-display tracking-[-0.035em] leading-[1.02] max-w-[16ch]"
        style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
      >
        Everything we do.
      </h1>
      <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
        Five ways to work with us: from idea to live product in 14 days, to an ongoing product team, plus rescue, consulting and migration. All shipped by full-stack designers who live in Lovable.
      </p>
      <div className="mt-10 md:mt-14 flex flex-wrap gap-3">
        <TalkToJonButton variant="dark" immersive />
        <MagneticButton href="/work" variant="outline-dark">
          See our work
        </MagneticButton>
      </div>
    </section>
  );
}

function Explainer() {
  return (
    <section
      aria-labelledby="what-is-h"
      className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-24"
    >
      <h2 id="what-is-h" className="fluid-h2 font-display max-w-[20ch]">
        What is a vibe coding agency, and when do you hire one?
      </h2>
      <div className="mt-10 grid md:grid-cols-2 gap-10 max-w-[110ch]">
        <p className="text-lg md:text-xl leading-relaxed text-foreground">
          A vibe coding agency builds software with AI coding tools — Lovable, Cursor, Claude Code,
          Codex — instead of hand-writing every line, then applies normal engineering discipline on
          top: real architecture, row-level security, tests, migrations, and someone accountable when
          it breaks. The AI writes fast. The engineer decides what's allowed to ship.
        </p>
        <p className="text-lg md:text-xl leading-relaxed text-foreground">
          You hire one when the prompting stops working: the app runs but the data is wide open, the
          build breaks every second change, the backend belongs to someone else, or you need it live
          by a date and can't afford to find out in week six. That's the whole job — five ways in,
          below. If you'd rather understand the method first, read{" "}
          <Link to="/guides/what-is-vibe-coding" className="text-electric underline underline-offset-4">
            what vibe coding actually is
          </Link>
          .
        </p>
      </div>
    </section>
  );
}

function Grid() {

  return (
    <section aria-labelledby="grid-h" className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-32">
      <h2 id="grid-h" className="fluid-h2 font-display max-w-[16ch]">Pick where you're stuck.</h2>
      <ul className="mt-14 grid md:grid-cols-2 gap-5">
        {SERVICES.map((s) => (
          <li key={s.k}>
            <Link
              to={s.to}
              className="group block h-full rounded-3xl border border-border bg-background p-8 md:p-12 transition-colors hover:bg-secondary/40"
            >
              <div className="flex items-baseline gap-5">
                <span
                  className="font-display text-electric leading-none"
                  style={{ fontSize: "clamp(2.5rem, 4vw, 4rem)" }}
                >
                  {s.k}
                </span>
                <h3
                  className="font-display tracking-[-0.03em] leading-[1.02]"
                  style={{ fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)" }}
                >
                  {s.t}
                </h3>
              </div>
              <p className="mt-6 text-base md:text-lg text-muted-foreground leading-snug max-w-[48ch]">
                {s.b}
              </p>
              <span className="mt-8 inline-flex items-center gap-2 text-sm text-foreground">
                {s.tag}
                <span aria-hidden className="inline-block transition-transform group-hover:translate-x-1">→</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function CTA() {
  return (
    <section aria-labelledby="cta-h" className="relative border-t border-border/60 bg-foreground text-background">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-40 grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 id="cta-h" className="font-display tracking-[-0.035em] leading-[1.02]" style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}>
            Send us the link.
          </h2>
          <p className="mt-8 text-lg md:text-xl text-background/70 max-w-[52ch] leading-snug">
             Bring your Lovable URL and where you're stuck. Jon will help shape the right next step.
          </p>
        </div>
        <div className="md:col-span-4 md:col-start-9">
          <a href="https://cal.com/vibinc/30min" target="_blank" rel="noopener noreferrer" className="block group">
            <span className="block font-display text-electric-bright break-all" style={{ fontSize: "clamp(1.75rem, 3vw, 2.75rem)" }}>
              Talk to Jon<span aria-hidden className="inline-block ml-2 transition-transform group-hover:translate-x-2">↗</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}
